import { useState, useEffect, useCallback } from 'react';
import type { AuthError, User, Session } from '@supabase/supabase-js';
import type { AuthCallResult } from '@/lib/auth-errors';
import { getContentOwnerBinding } from '@/lib/content-owner';
import { hasPositiveUnsyncedOwnerWork } from '@/lib/owner-bootstrap';
import { syncContentLifecycle } from '@/lib/content-lifecycle/sync';
import { noteAuthenticatedSession } from '@/lib/email-signin-resume';
import { getSupabase } from '@/lib/supabase';
import { hasActiveVoiceCaptureWork } from '@/lib/voice-work-fence';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

interface UseAuthReturn extends AuthState {
  signInWithGoogle: (options?: { selectAccount?: boolean }) => Promise<{ error: string | null }>;
  signInWithApple: () => Promise<{ error: string | null }>;
  signInWithEmail: (email: string) => Promise<AuthCallResult>;
  verifyOtp: (email: string, token: string) => Promise<AuthCallResult>;
  signOut: (options?: { force?: boolean }) => Promise<void>;
}

export class OwnerWorkPendingError extends Error {
  constructor() {
    super('owner-work-pending');
    this.name = 'OwnerWorkPendingError';
  }
}

/**
 * The email calls keep `error` (the raw message) exactly as before and add the
 * provider's own details so AuthModal can tell a dropped connection, server
 * trouble, a rate limit and a bad code apart. Status 0 on a retryable error
 * means the request got no response.
 */
function emailCallResult(error: AuthError | null): AuthCallResult {
  if (!error) return { error: null };
  return {
    error: error.message,
    code: typeof error.code === 'string' ? error.code : undefined,
    status: typeof error.status === 'number' ? error.status : undefined,
    retryable: error.name === 'AuthRetryableFetchError',
  };
}

export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>(() =>
    getSupabase()
      ? { user: null, session: null, loading: true }
      : { user: null, session: null, loading: false },
  );

  useEffect(() => {
    const sb = getSupabase();
    if (!sb) return;

    sb.auth
      .getSession()
      .then(({ data: { session } }) => {
        // Storage/memory only: a signed-in session ends any email-code resume
        // (no auth call; see email-signin-resume.ts).
        if (session?.user) noteAuthenticatedSession();
        setState({ user: session?.user ?? null, session, loading: false });
      })
      .catch(() => {
        // The initial session check must always SETTLE (2026-08-25): a
        // rejection here previously left `loading` true forever, which
        // parks AuthGate (when the hard gate is enabled) on its loading
        // screen indefinitely. Fail to signed-out; onAuthStateChange
        // corrects the state the moment a real session surfaces.
        setState({ user: null, session: null, loading: false });
      });

    const { data: { subscription } } = sb.auth.onAuthStateChange(
      (_event, session) => {
        if (session?.user) noteAuthenticatedSession();
        setState({ user: session?.user ?? null, session, loading: false });
      },
    );

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = useCallback(async (options?: { selectAccount?: boolean }) => {
    const sb = getSupabase();
    if (!sb) return { error: 'Auth not configured' };
    const { error } = await sb.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        ...(options?.selectAccount
          ? { queryParams: { prompt: 'select_account' } }
          : {}),
      },
    });
    return { error: error?.message ?? null };
  }, []);

  const signInWithApple = useCallback(async () => {
    const sb = getSupabase();
    if (!sb) return { error: 'Auth not configured' };
    const { error } = await sb.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo: window.location.origin },
    });
    return { error: error?.message ?? null };
  }, []);

  const signInWithEmail = useCallback(async (email: string) => {
    const sb = getSupabase();
    if (!sb) return { error: 'Auth not configured' };
    // Code-only email (no magic link), so no emailRedirectTo: the email carries
    // the 6-digit OTP and nothing to redirect. The link + the code share one
    // single-use token, so shipping a link would let a tapped link consume the
    // code (cross-device footgun); the email template is code-only to match.
    const { error } = await sb.auth.signInWithOtp({ email });
    return emailCallResult(error);
  }, []);

  const verifyOtp = useCallback(async (email: string, token: string) => {
    const sb = getSupabase();
    if (!sb) return { error: 'Auth not configured' };
    const { error } = await sb.auth.verifyOtp({ email, token, type: 'email' });
    return emailCallResult(error);
  }, []);

  const signOut = useCallback(async (options?: { force?: boolean }) => {
    const sb = getSupabase();
    if (!sb) return;
    if (!options?.force) {
      if (hasActiveVoiceCaptureWork()) throw new OwnerWorkPendingError();
      const { data } = await sb.auth.getSession();
      const ownerId = data.session?.user.id ?? null;
      const binding = await getContentOwnerBinding().catch(() => null);
      if (ownerId && binding === ownerId && await hasPositiveUnsyncedOwnerWork(ownerId)) {
        if (typeof navigator === 'undefined' || !navigator.onLine) {
          throw new OwnerWorkPendingError();
        }
        try {
          await syncContentLifecycle();
        } catch {
          throw new OwnerWorkPendingError();
        }
        if (await hasPositiveUnsyncedOwnerWork(ownerId)) {
          throw new OwnerWorkPendingError();
        }
      }
      if (hasActiveVoiceCaptureWork()) throw new OwnerWorkPendingError();
    }
    await sb.auth.signOut();
  }, []);

  return { ...state, signInWithGoogle, signInWithApple, signInWithEmail, verifyOtp, signOut };
}
